import { MoreVertIcon } from "../../images/MoreVertIcon";
import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { useState } from "react";
import { StyledMenu } from "../StyledMenu";
import {
  useRemoveAccountPlanMutation,
  useRemoveAccountPlaybooksMutation,
} from "../../../api";
import { IconWrapper } from "../IconWrapper";
import { toast } from "react-toastify";
import { CustomToast } from "../CustomToast";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { ReuseModal } from "../ReuseModal";
import { useAppSelector } from "../../../hooks";
import { selectPermissions } from "../../../features/permissionsSlice";
import { PlaybookPlanNames } from "../../../types/enums";

export const AccountPlanCardMenu = ({
  id,
  name,
  campaign_id,
  isPlaybook,
  onSuccess,
  account_id,
  btnHeight,
}: {
  id: string;
  name: string;
  campaign_id: string;
  account_id: string;
  isPlaybook: boolean;
  onSuccess: () => void;
  btnHeight: string;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { isAllowedEditGenerateReports } = useAppSelector(selectPermissions);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [deleteAccountPlan, { isLoading: isDeletingPAccountPlan }] =
    useRemoveAccountPlanMutation();
  const [deletePlaybook, { isLoading: isDeletingPlaybook }] =
    useRemoveAccountPlaybooksMutation();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };

  const deleteHandler = () => {
    (isPlaybook
      ? deletePlaybook({
          account_id,
          campaign_id,
          members_ids: [id],
          include_all: false,
        })
      : deleteAccountPlan({ id, campaign_id })
    )
      .unwrap()
      .then(onSuccess)
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={`Failed to Delete ${isPlaybook ? PlaybookPlanNames.playbook : PlaybookPlanNames.account_plan}`}
          />,
        );
      });
    handleClose();
  };

  const isLoading = isDeletingPAccountPlan || isDeletingPlaybook;

  if (isLoading) {
    return (
      <IconWrapper width={"40px"} height={"40px"}>
        <CircularProgress size={20} sx={{ color: "#1C3C6C" }} />
      </IconWrapper>
    );
  }

  return (
    <>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        sx={{ height: btnHeight }}
      >
        <MoreVertIcon width="24" />
      </IconButton>
      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{
          "aria-labelledby": "demo-customized-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "white",
          },
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          style: {
            transform: "translateX(12px)",
          },
        }}
      >
        <MenuItem
          sx={{ display: "flex", gap: "12px", alignItems: "center" }}
          onClick={() => setIsOpen(true)}
          disabled={!isAllowedEditGenerateReports}
        >
          Delete
        </MenuItem>
      </StyledMenu>
      <ReuseModal
        isOpen={isOpen}
        onClose={handleClose}
        onSubmit={deleteHandler}
        title={`Remove ${isPlaybook ? PlaybookPlanNames.playbook : PlaybookPlanNames.account_plan}`}
        customText={
          <Stack gap="32px">
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to remove the{" "}
              {isPlaybook
                ? PlaybookPlanNames.playbook
                : PlaybookPlanNames.account_plan}{" "}
              for {name}?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You’ll need to generate it again if you need it later.
            </Typography>
          </Stack>
        }
        isDisabled={isLoading}
        isLoading={isLoading}
        submitBtnText="Remove"
        maxWidth="430px"
      />
    </>
  );
};
